import API from "./api";
import API_ROUTES from "./apiRoutes";

const AUTH_STORAGE_KEY = "myasmita:auth-user";

export const normalizeRoleKey = (value = "") =>
  String(value)
    .trim()
    .toLowerCase()
    .replace(/[_\s]+/g, "-");

export const getStoredAuthUser = () => {
  try {
    const rawValue = localStorage.getItem(AUTH_STORAGE_KEY);
    return rawValue ? JSON.parse(rawValue) : null;
  } catch (error) {
    console.error("GET STORED AUTH USER ERROR:", error);
    return null;
  }
};

export const getCurrentRoleKey = () => {
  const user = getStoredAuthUser();
  return normalizeRoleKey(user?.system_role || user?.primary_role || user?.user_type || user?.role || "");
};

export const isTokenExpired = (token) => {
  if (!token || typeof token !== "string") return true;
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return false; // Non-JWT format (e.g. mock/opaque token)
    const base64Url = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64Url)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
    const parsed = JSON.parse(jsonPayload);
    if (parsed.exp && typeof parsed.exp === "number") {
      // 10 second buffer
      return Date.now() >= (parsed.exp * 1000) - 10000;
    }
    return false;
  } catch {
    return false;
  }
};

export const isAuthenticated = () => {
  const token = localStorage.getItem("token");
  const user = getStoredAuthUser();
  if (!token || !user) {
    return false;
  }
  if (isTokenExpired(token)) {
    clearAuthSession();
    return false;
  }
  return true;
};

export const getSafeRedirect = (targetUrl, fallback = "/dashboard") => {
  if (!targetUrl || typeof targetUrl !== "string") {
    return fallback;
  }

  const trimmed = targetUrl.trim();
  if (!trimmed) {
    return fallback;
  }

  // Prevent protocol-relative URLs (e.g. "//evil.com") and backslash evasions (e.g. "/\\evil.com")
  if (!trimmed.startsWith("/") || trimmed.startsWith("//") || trimmed.startsWith("/\\") || trimmed.startsWith("/%5c") || trimmed.startsWith("/%5C")) {
    return fallback;
  }

  // Reject newlines, tabs, carriage returns, or malicious pseudo-protocols
  if (/[\r\n\t]/.test(trimmed)) {
    return fallback;
  }
  if (/^(?:javascript|data|vbscript|file|about):/i.test(trimmed)) {
    return fallback;
  }

  try {
    const origin = typeof window !== "undefined" ? window.location.origin : "http://localhost";
    const parsed = new URL(trimmed, origin);

    // Host and origin MUST strictly match our application's current origin
    if (parsed.origin !== origin) {
      return fallback;
    }

    // Disallow redirecting back to login root or auth routes (avoids infinite redirection loops)
    if (parsed.pathname === "/" || parsed.pathname === "/login") {
      return fallback;
    }

    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return fallback;
  }
};

export const setAuthSession = ({ token, user }) => {
  localStorage.setItem("token", token);
  localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(user));
};

export const clearAuthSession = () => {
  localStorage.removeItem("token");
  localStorage.removeItem(AUTH_STORAGE_KEY);
};

export const logout = async () => {
  const user = getStoredAuthUser();
  try {
    await API.post(API_ROUTES.auth.logout, {
      user_id: user?.user_id,
      system_role: user?.system_role,
      primary_role: user?.primary_role,
      account_type: user?.account_type || "app",
      os_type: user?.os_type || "Android",
      profile_picture: user?.profile_picture,
      society_id: user?.society_id || null,
    });
  } catch (error) {
    console.error("LOGOUT API ERROR:", error);
  } finally {
    clearAuthSession();
  }
};

export const requestPasswordReset = async ({ email, society_id } = {}) => {
  const payload = {
    email: String(email || "").trim().toLowerCase(),
  };

  if (society_id !== undefined && society_id !== null && String(society_id).trim() !== "") {
    payload.society_id = society_id;
  }

  const res = await API.post(API_ROUTES.auth.requestPasswordReset, payload);
  return res.data;
};

export const resetPassword = async ({ user_id, token, new_password } = {}) => {
  const payload = {
    user_id: Number(user_id),
    token: String(token || "").trim(),
    new_password: String(new_password || "").trim(),
  };

  const res = await API.post(API_ROUTES.auth.resetPassword, payload);
  return res.data;
};
